import { create } from 'zustand';
import { apiClient, API_ENDPOINTS } from '../lib';

type NotificationType = 'SYSTEM' | 'PAYMENT' | 'AI';

interface Notification {
  id: number;
  userId: number;
  title: string;
  content: string;
  imageUrl: string | null;
  link: string | null;
  htmlContent: string | null;
  type: NotificationType;
  isRead: boolean;
  createdAt: string;
}

interface NotificationListResponse {
  notifications: Notification[];
  total: number;
}

interface UnreadCountResponse {
  count: number;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  fetchNotifications: (token: string, page?: number, limit?: number) => Promise<void>;
  fetchUnreadCount: (token: string) => Promise<void>;
  markAsRead: (token: string, id: number) => Promise<void>;
  markAllAsRead: (token: string) => Promise<void>;
  deleteNotification: (token: string, id: number) => Promise<void>;
  clearNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchNotifications: async (token, page = 1, limit = 20) => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await apiClient.get<NotificationListResponse>(
        `${API_ENDPOINTS.notifications.list}?page=${page}&limit=${limit}`,
        token
      );

      set({
        notifications: data.notifications || [],
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '获取通知失败',
        isLoading: false,
      });
    }
  },

  fetchUnreadCount: async (token) => {
    try {
      const { data } = await apiClient.get<UnreadCountResponse>(
        API_ENDPOINTS.notifications.unreadCount,
        token
      );

      set({ unreadCount: data.count });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (token, id) => {
    try {
      await apiClient.put(API_ENDPOINTS.notifications.markAsRead(id), undefined, token);

      const { notifications, unreadCount } = get();
      set({
        notifications: notifications.map((n) =>
          n.id === id ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, unreadCount - 1),
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '标记已读失败',
      });
    }
  },

  markAllAsRead: async (token) => {
    try {
      await apiClient.put(API_ENDPOINTS.notifications.markAllAsRead, undefined, token);

      const { notifications } = get();
      set({
        notifications: notifications.map((n) => ({ ...n, isRead: true })),
        unreadCount: 0,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '标记全部已读失败',
      });
    }
  },

  deleteNotification: async (token, id) => {
    try {
      await apiClient.delete(API_ENDPOINTS.notifications.delete(id), token);

      const { notifications, unreadCount } = get();
      const notification = notifications.find((n) => n.id === id);
      set({
        notifications: notifications.filter((n) => n.id !== id),
        unreadCount: notification && !notification.isRead ? unreadCount - 1 : unreadCount,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : '删除通知失败',
      });
    }
  },

  clearNotifications: () => {
    set({ notifications: [], unreadCount: 0 });
  },
}));

export type { Notification, NotificationType };
